import { useQuery } from '@tanstack/react-query';

import type { DtoV1BoltResponseModelsAssetsDepositoryAccountScreenDataDictionarySystemStringSystemObject } from '~/__generated__/api';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';

export const keys = {
  all: ['getAccountSummaryInformation'],
  forAsset: (assetId: number | string) => [
    ...keys.all,
    Number.parseInt(assetId as string),
  ],
};

export type AccountSummaryInformationData = ReturnType<
  typeof useGetAccountSummaryInformation
>['data'];

const useGetAccountSummaryInformation = (assetId) => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { loanStatus } = useGetLoanSummary().data?.loanInfo || {};

  return useQuery({
    queryKey: keys.forAsset(assetId),
    queryFn: async () => {
      const response =
        await typedApiClient.v1.accountInformationGetAccountSummaryInformation({
          obfuscatedLoanIdentifier,
          assetId: assetId,
        });

      const data =
        response.data as DtoV1BoltResponseModelsAssetsDepositoryAccountScreenDataDictionarySystemStringSystemObject & {
          screenData: Record<string, any>;
        };

      data.screenData.documentOptions =
        data?.screenData?.pdfViewerDocumentDropdownOptions;

      data.screenData.asset.accountNumber =
        data?.screenData?.asset?.accountNumber?.trim();

      data.screenData.doesAccountNumberExist =
        !!data.screenData.asset.accountNumber;

      data.screenData.loanStatus = loanStatus;

      data.screenData.lastFourAccountNumber =
        data?.screenData?.asset?.accountNumber?.substring(
          data.screenData.asset.accountNumber.length - 4,
        );

      data.screenData.bankNameOrAssetType =
        data?.screenData?.asset?.bankName || data?.screenData?.asset?.assetType;

      data.screenData.isFinancialInstitutionMissingInDocument =
        data.initialFormValues?.isFinancialInstitutionMissingInDocument ??
        false;

      if (data?.screenData?.extractedData?.financialInstitution?.value) {
        data.screenData.extractedData.financialInstitution.value = data
          ?.screenData?.extractedData?.financialInstitution?.value
          ? data?.screenData?.extractedData?.financialInstitution?.value
          : 'Not Available';
      }

      if (data?.screenData?.extractedData?.assetType?.value) {
        data.screenData.extractedData.assetType.value = data?.screenData
          ?.extractedData?.assetType?.value
          ? data?.screenData?.extractedData?.assetType?.value
          : 'Not Available';
      }

      if (
        data.screenData?.asset?.accountNumber === '' &&
        data.screenData?.assetsFeatureFlags?.assetFeatureFlags
          ?.AssetsScreenAccountNumberRequiredEffectiveDate &&
        !data?.initialFormValues?.isAccountNumberAnAcceptableMatch
      ) {
        data.initialFormValues.isAccountNumberAnAcceptableMatch = 'No';
      }

      return {
        ...data,
      };
    },

    cacheTime: 0,
  });
};

export default useGetAccountSummaryInformation;
